import React, { useState } from 'react';
import { 
  Pill, 
  Heart, 
  Activity, 
  Users, 
  Clock, 
  Plus, 
  History,
  ShieldAlert,
  BabyIcon,
  Stethoscope,
  Trash2,
  Edit2,
  Loader2,
  Save,
  X
} from 'lucide-react';
import { AppointmentSummaryResponse } from './types';
import { PatientService } from '../../../services/api/patient.service';
import toast from 'react-hot-toast';

interface HealthRecordsTabProps {
  data: AppointmentSummaryResponse['appointmentSummary'];
  reload: () => void;
}

const HealthRecordsTab: React.FC<HealthRecordsTabProps> = ({ data, reload }) => {
  const { patientHealthRecords, patient } = data.appointmentSummary;
  const isFemale = patient?.gender?.toLowerCase() === 'female';

  const categories = [
    { label: 'Allergies', icon: ShieldAlert, key: 'Allergies', p: 'Enter Allergy Name', s: 'Enter Allergy Symptoms & Causes' },
    { label: 'Drug/Medications', icon: Pill, key: 'Drug/Medications', p: 'Enter Medicine Name', s: 'Enter Medicine Uses' },
    { label: 'Medical Conditions', icon: Heart, key: 'Medical Conditions', p: 'Enter Medical Condition Name', s: 'Enter Condition Details' },
    { label: 'Surgical History', icon: History, key: 'Surgical History', p: 'Enter Surgery Name', s: 'Enter Surgery Details' },
    { label: 'Family History', icon: Users, key: 'Family Medical History', p: "Enter Family Member's Medical Condition", s: 'Enter Relationship and History' },
    { label: 'Social History', icon: Activity, key: 'Social History', p: 'Enter Social History', s: 'Enter Relevant Social Factors' },
    { label: 'Vaccinations', icon: ShieldAlert, key: 'Vaccinations', p: 'Enter Vaccination Name', s: 'Enter Vaccination Details' },
    { label: 'Mental History', icon: Activity, key: 'Mental/Psychiatric History', p: 'Enter Mental Health Condition', s: 'Enter Relevant History' },
  ];

  if (isFemale) {
    categories.push({ label: 'Gynecologic Records', icon: BabyIcon, key: 'Gynecologic Records', p: 'Enter Gynecologic Record', s: 'Enter Relevant History' });
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between border-b border-gray-100 pb-6">
        <div className="flex items-center gap-3">
          <Stethoscope className="h-6 w-6 text-emerald-600" />
          <h2 className="text-xl font-bold text-gray-900">Health History</h2>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories.map((cat) => (
          <RecordCategoryBox 
            key={cat.key}
            category={cat}
            records={patientHealthRecords?.[cat.key] || []}
            patientId={patient?.id}
            reload={reload}
          />
        ))}
      </div>
    </div>
  );
};

const RecordCategoryBox = ({ category, records, patientId, reload }: any) => {
  const Icon = category.icon;
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedSocialHistory, setSelectedSocialHistory] = useState('Smoking');
  
  const [formData, setFormData] = useState({
    id: '',
    health_value: '',
    health_value_description: '',
    start_date: '',
    end_date: '',
    current_state: '',
    daily_consumption: ''
  });

  const resetForm = () => {
    setFormData({
      id: '', health_value: '', health_value_description: '',
      start_date: '', end_date: '', current_state: '', daily_consumption: ''
    });
    setShowForm(false);
    setIsEditing(false);
  };

  const handleShowAdd = () => {
    resetForm();
    setShowForm(true);
  };

  const handleShowEdit = (record: any) => {
    setFormData({
      id: record.id,
      health_value: record.health_value || '',
      health_value_description: record.health_value_description || '',
      start_date: record.health_value_start_date ? record.health_value_start_date.split('T')[0] : '',
      end_date: record.health_value_end_date ? record.health_value_end_date.split('T')[0] : '',
      current_state: record.current_state || '',
      daily_consumption: record.daily_consumption || ''
    });
    if (category.key === 'Social History') {
       setSelectedSocialHistory(record.health_value || 'Smoking');
    }
    setIsEditing(true);
    setShowForm(true);
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleISODate = (date: string) => {
    if (!date) return null;
    return new Date(date).toISOString();
  };

  const buildPayload = () => {
    return {
      health_parameter: category.key,
      health_value: category.key === 'Social History' ? selectedSocialHistory : formData.health_value,
      health_value_description: formData.health_value_description,
      current_state: formData.current_state || null,
      daily_consumption: formData.daily_consumption || null,
      start_date: handleISODate(formData.start_date),
      end_date: handleISODate(formData.end_date)
    };
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const payload = buildPayload();
      const response = await PatientService.addPatientHealthRecord(patientId, payload);
      if (response.success) {
        toast.success(response.message || "Record added successfully");
        resetForm();
        reload();
      } else {
        toast.error(response.message || "Failed to add record");
      }
    } catch (error: any) {
      toast.error("Error while saving: " + (error?.message || "Unknown error"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdate = async () => {
    setIsSaving(true);
    try {
      const payload = buildPayload();
      const response = await PatientService.updatePatientHealthRecord(formData.id, payload);
      if (response.success) {
        toast.success(response.message || "Record updated successfully");
        resetForm();
        reload();
      } else {
        toast.error(response.message || "Failed to update record");
      }
    } catch (error: any) {
      toast.error("Error while updating: " + (error?.message || "Unknown error"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this record?")) return;
    try {
      const response = await PatientService.deletePatientHealthRecord(id);
      if (response.success) {
        toast.success(response.message || "Record deleted successfully");
        reload();
      } else {
        toast.error(response.message || "Failed to delete record");
      }
    } catch (error: any) {
      toast.error("Error while deleting: " + (error?.message || "Unknown error"));
    }
  };

  const filteredRecords = records?.filter((r: any) => 
    category.key === 'Social History' ? r.health_value === selectedSocialHistory : true
  ) || [];

  return (
    <div className="bg-gray-50 rounded-2xl border border-gray-100 flex flex-col h-[380px] overflow-hidden">
      <div className="p-4 bg-white border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-emerald-600" />
          <span className="text-sm font-bold text-gray-700">{category.label}</span>
        </div>
        {!showForm ? (
          <button onClick={handleShowAdd} className="p-1.5 hover:bg-emerald-50 rounded-full text-emerald-600 transition-colors tooltip" title="Add Record">
            <Plus className="h-4 w-4" />
          </button>
        ) : (
          <button onClick={resetForm} className="p-1.5 hover:bg-red-50 rounded-full text-red-500 transition-colors tooltip" title="Cancel">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {category.key === 'Social History' && !showForm && (
        <div className="flex items-center gap-2 p-3 bg-white border-b border-gray-50">
          {['Smoking', 'Drinking', 'Tobacco'].map(tab => (
            <button
              key={tab}
              onClick={() => setSelectedSocialHistory(tab)}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${selectedSocialHistory === tab ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              {tab}
            </button>
          ))}
        </div>
      )}
      
      {showForm ? (
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-white hiddenScrollbar">
            {category.key !== 'Social History' ? (
              <input
                type="text"
                name="health_value"
                value={formData.health_value}
                onChange={handleOnChange}
                placeholder={category.p || `Enter ${category.label}`}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-emerald-500"
              />
            ) : (
              <>
                <div className="flex gap-2">
                  {['Smoking', 'Drinking', 'Tobacco'].map(tab => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setSelectedSocialHistory(tab)}
                      className={`flex-1 py-1.5 text-xs font-medium rounded-lg border transition-colors ${selectedSocialHistory === tab ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                    >
                      {tab}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  name="current_state"
                  value={formData.current_state}
                  onChange={handleOnChange}
                  placeholder="Enter Current State"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-emerald-500"
                />
                <input
                  type="text"
                  name="daily_consumption"
                  value={formData.daily_consumption}
                  onChange={handleOnChange}
                  placeholder="Enter Daily Consumption"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-emerald-500"
                />
              </>
            )}
            
            {category.key !== 'Medical Conditions' && (
              <textarea
                name="health_value_description"
                value={formData.health_value_description}
                onChange={handleOnChange}
                placeholder={category.s || "Enter Description"}
                className="w-full min-h-[80px] px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-emerald-500 resize-y"
              />
            )}
            
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="text-[10px] uppercase font-bold text-gray-500 mb-1 block">Start Date</label>
                <input
                  type="date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleOnChange}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-emerald-500"
                />
              </div>
              {category.key === 'Social History' && (
                <div className="flex-1">
                  <label className="text-[10px] uppercase font-bold text-gray-500 mb-1 block">End Date</label>
                  <input
                    type="date"
                    name="end_date"
                    value={formData.end_date}
                    onChange={handleOnChange}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                </div>
              )}
            </div>
            
            <div className="pt-2 pb-2">
              <button
                onClick={isEditing ? handleUpdate : handleSave}
                disabled={isSaving}
                className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors flex justify-center items-center gap-2 disabled:opacity-70"
              >
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {isSaving ? "Saving..." : (isEditing ? "Update Record" : "Save Record")}
              </button>
            </div>
          </div>
      ) : (
        <div className="flex-1 overflow-y-auto p-4 space-y-3 hiddenScrollbar">
          {filteredRecords.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-gray-400 opacity-60 min-h-[200px]">
              <Clock className="h-8 w-8 mb-2" />
              <p className="text-xs font-medium">No records found</p>
            </div>
          ) : (
            filteredRecords.map((record: any) => (
              <div key={record.id} className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm relative group">
                <div className="flex justify-between items-start mb-1">
                  <p className="text-sm font-bold text-gray-900 pr-12">{record.health_value}</p>
                  <div className="flex gap-1 absolute top-3 right-3 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity bg-white">
                    <button onClick={() => handleShowEdit(record)} title="Edit" className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-md transition-colors">
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => handleDelete(record.id)} title="Delete" className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                
                {category.key === 'Social History' && record.current_state && (
                  <p className="text-xs text-gray-600 font-medium mb-1">State: <span className="text-gray-900">{record.current_state}</span></p>
                )}
                
                {record.health_value_description && (
                  <p className="text-xs text-gray-500 mt-1">{record.health_value_description}</p>
                )}
                
                <div className="flex gap-3 mt-3 pt-2 border-t border-gray-50">
                  {record.health_value_start_date && (
                    <p className="text-[10px] text-gray-400 font-medium flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                      {new Date(record.health_value_start_date).toLocaleDateString()}
                    </p>
                  )}
                  {record.health_value_end_date && category.key === 'Social History' && (
                    <p className="text-[10px] text-gray-400 font-medium flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-400"></span>
                      {new Date(record.health_value_end_date).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default HealthRecordsTab;
